import '@fontsource/lato';
import '@fontsource-variable/inter';
import React, { useContext, useEffect } from 'react';

import { CssBaseline } from '@mui/joy';
import Box from '@mui/joy/Box';
import CircularProgress from '@mui/joy/CircularProgress';
import { CssVarsProvider } from '@mui/joy/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';

import { AppGates } from './components/AppGates';
import { ErrorFallback } from './components/ErrorFallback';
import { GlobalCustomCSSProvider } from './components/GlobalCustomCSSProvider';
import { OnboardingRoot } from './components/onboarding/OnboardingRoot';
import SnackBar from './components/SnackBar';
import AuthContext from './context/authContext';
import { EmailVerificationProvider } from './context/emailVerificationContext';
import { PermissionsProvider } from './context/permissionsContext';
import { PermissionsContext } from './context/permissionsContext';
import { Routes } from './routes';
import { assetBaseUrl } from './services/axios/endpoints';
import queryClient from './services/react-query';
import { theme } from './theme';
import { EMAIL_VERIFICATION_PERIOD_DAYS } from './utils/constants/emailVerification';
import { preloadCommonProjectIcons } from './utils/imagePreloader';

function AppLoadingGate({ children }: { children: React.ReactNode }) {
  const { loading } = useContext(PermissionsContext);

  if (loading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' height='100vh' width='100vw'>
        <CircularProgress />
      </Box>
    );
  }
  return <>{children}</>;
}

function App() {
  useEffect(() => {
    if (assetBaseUrl) {
      preloadCommonProjectIcons(assetBaseUrl).catch(console.warn);
    }
  }, []);

  return (
    <AuthContext>
      <QueryClientProvider client={queryClient}>
        <PermissionsProvider>
          <CssVarsProvider theme={theme} defaultMode='light'>
            <CssBaseline />
            <GlobalCustomCSSProvider>
              <EmailVerificationProvider verificationPeriodDays={EMAIL_VERIFICATION_PERIOD_DAYS}>
                <AppLoadingGate>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <AppGates>
                      <Routes />
                      <SnackBar />
                      <OnboardingRoot />
                    </AppGates>
                  </ErrorBoundary>
                </AppLoadingGate>
              </EmailVerificationProvider>
            </GlobalCustomCSSProvider>
          </CssVarsProvider>
        </PermissionsProvider>
      </QueryClientProvider>
    </AuthContext>
  );
}

export default App;
